/* eslint-disable react/no-unescaped-entities */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowLeft, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { SITE } from "@/constants/site";

function ServiceIcon({ name, className }) {
    const Cmp = Icons[name] || Icons.Briefcase;
    return <Cmp className={className} />;
}

const BUDGETS = [
    "Under $2,500",
    "$2,500 – $5,000",
    "$5,000 – $15,000",
    "$15,000 – $50,000",
    "$50,000+",
    "Not sure yet",
];
const TIMELINES = [
    "Immediate (this month)",
    "1–3 months",
    "3–6 months",
    "6+ months",
    "Just exploring",
];

export default function ServiceDetail() {
    const { slug } = useParams();
    const [service, setService] = useState(null);
    const [allServices, setAllServices] = useState([]);
    const [notFound, setNotFound] = useState(false);

    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        organization: "",
        budget_range: "",
        timeline: "",
        message: "",
    });
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        api.get("/services")
            .then(({ data }) => {
                setAllServices(data);
                const found = data.find((s) => s.slug === slug);
                if (found) setService(found);
                else setNotFound(true);
            })
            .catch(() => setNotFound(true));
    }, [slug]);

    const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        setSubmitting(true);
        try {
            await api.post("/leads", {
                ...form,
                service_needed: service.title,
            });
            setSubmitted(true);
            toast.success("Request received — Reico will be in touch.");
        } catch (err) {
            const msg = formatApiErrorDetail(err.response?.data?.detail) || err.message;
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    if (notFound) {
        return (
            <div className="max-w-3xl mx-auto px-6 py-32 text-center">
                <h1 className="font-serif text-3xl font-bold text-[#0A192F]">
                    Service not found
                </h1>
                <Link to="/services" className="mt-6 inline-flex items-center gap-2 text-[#D4AF37] font-semibold">
                    <ArrowLeft className="w-4 h-4" /> Back to services
                </Link>
            </div>
        );
    }

    if (!service) {
        return (
            <div className="py-32 text-center text-slate-500 text-sm uppercase tracking-widest">
                Loading…
            </div>
        );
    }

    const related = allServices.filter((s) => s.slug !== service.slug).slice(0, 3);

    return (
        <div data-testid={`service-detail-${service.slug}`}>
            {/* Hero */}
            <section className="bg-[#0A192F] text-white py-24">
                <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12">
                    <Link
                        to="/services"
                        data-testid="service-detail-back"
                        className="text-xs uppercase tracking-widest text-[#D4AF37] inline-flex items-center gap-2 mb-8"
                    >
                        <ArrowLeft className="w-3.5 h-3.5" /> All Services
                    </Link>
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 bg-white/10 border border-white/20 flex items-center justify-center rounded-sm">
                            <ServiceIcon name={service.icon} className="w-6 h-6 text-[#D4AF37]" />
                        </div>
                        <span className="overline">Service</span>
                    </div>
                    <h1 className="font-serif text-5xl sm:text-6xl font-bold mt-6 leading-tight">
                        {service.title}
                    </h1>
                    <p className="mt-6 text-lg text-slate-300 max-w-2xl leading-relaxed">
                        {service.description}
                    </p>
                </div>
            </section>

            {/* Details + Form */}
            <section className="py-24">
                <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 grid lg:grid-cols-12 gap-16">
                    {/* Left: details */}
                    <div className="lg:col-span-5 space-y-12">
                        {service.who_it_is_for && (
                            <div>
                                <span className="overline">Who it's for</span>
                                <p className="mt-4 text-lg text-slate-700 leading-relaxed">
                                    {service.who_it_is_for}
                                </p>
                            </div>
                        )}

                        {service.deliverables?.length > 0 && (
                            <div>
                                <span className="overline">What you receive</span>
                                <ul className="mt-5 space-y-3">
                                    {service.deliverables.map((d) => (
                                        <li
                                            key={d}
                                            className="flex items-start gap-3 text-slate-700"
                                        >
                                            <CheckCircle2 className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                                            <span>{d}</span>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        <div className="border-l-2 border-[#D4AF37] pl-6">
                            <p className="text-slate-700 italic leading-relaxed">
                                Every engagement starts with a free 30-minute
                                strategy call. We listen, ask the sharp
                                questions, and map your next three moves —
                                before you commit to anything.
                            </p>
                        </div>

                        <div className="border-t border-slate-200 pt-6">
                            <p className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-3">
                                Or call directly
                            </p>
                            <a
                                href={`tel:${SITE.phoneLink}`}
                                data-testid="service-detail-phone"
                                className="font-serif text-2xl font-bold text-[#0A192F] hover:text-[#D4AF37] transition-colors"
                            >
                                {SITE.phone}
                            </a>
                        </div>
                    </div>

                    {/* Right: inline form */}
                    <div className="lg:col-span-7">
                        <div className="exec-card p-8 lg:p-10">
                            {submitted ? (
                                <div className="text-center py-10" data-testid="service-form-success">
                                    <CheckCircle2 className="w-12 h-12 text-[#D4AF37] mx-auto mb-6" />
                                    <h2 className="font-serif text-2xl font-bold text-[#0A192F]">
                                        Request received.
                                    </h2>
                                    <p className="mt-3 text-slate-600 max-w-md mx-auto">
                                        Reico will review your request for{" "}
                                        <strong>{service.title}</strong> and
                                        follow up within one business day.
                                    </p>
                                    <Link
                                        to="/services"
                                        className="mt-8 inline-flex items-center gap-2 text-[#0A192F] font-semibold link-underline pb-1"
                                    >
                                        Browse more services <ArrowRight className="w-4 h-4" />
                                    </Link>
                                </div>
                            ) : (
                                <form onSubmit={onSubmit} className="space-y-6" data-testid="service-detail-form">
                                    <div>
                                        <span className="overline">Request this service</span>
                                        <h2 className="font-serif text-3xl font-bold mt-3 text-[#0A192F]">
                                            Start with a free strategy call.
                                        </h2>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-6">
                                        <div>
                                            <label className="exec-input-label">Name</label>
                                            <input
                                                required
                                                type="text"
                                                data-testid="sd-input-name"
                                                className="exec-input"
                                                value={form.name}
                                                onChange={(e) => update("name", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="exec-input-label">Email</label>
                                            <input
                                                required
                                                type="email"
                                                data-testid="sd-input-email"
                                                className="exec-input"
                                                value={form.email}
                                                onChange={(e) => update("email", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="exec-input-label">Phone</label>
                                            <input
                                                type="tel"
                                                data-testid="sd-input-phone"
                                                className="exec-input"
                                                value={form.phone}
                                                onChange={(e) => update("phone", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="exec-input-label">Organization</label>
                                            <input
                                                type="text"
                                                data-testid="sd-input-org"
                                                className="exec-input"
                                                value={form.organization}
                                                onChange={(e) => update("organization", e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="exec-input-label">Budget Range</label>
                                            <select
                                                data-testid="sd-input-budget"
                                                className="exec-input"
                                                value={form.budget_range}
                                                onChange={(e) => update("budget_range", e.target.value)}
                                            >
                                                <option value="">Select…</option>
                                                {BUDGETS.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                        <div>
                                            <label className="exec-input-label">Timeline</label>
                                            <select
                                                data-testid="sd-input-timeline"
                                                className="exec-input"
                                                value={form.timeline}
                                                onChange={(e) => update("timeline", e.target.value)}
                                            >
                                                <option value="">Select…</option>
                                                {TIMELINES.map((s) => (
                                                    <option key={s} value={s}>{s}</option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                    <div>
                                        <label className="exec-input-label">Tell us where you are</label>
                                        <textarea
                                            required
                                            rows={4}
                                            data-testid="sd-input-message"
                                            className="exec-input resize-none"
                                            value={form.message}
                                            onChange={(e) => update("message", e.target.value)}
                                            placeholder="Where you're starting, what you've tried, and what success looks like."
                                        />
                                    </div>
                                    {error && (
                                        <p className="text-sm text-red-600" data-testid="sd-error">
                                            {error}
                                        </p>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={submitting}
                                        data-testid="sd-submit"
                                        className="w-full inline-flex items-center justify-center gap-3 bg-[#0A192F] text-white hover:bg-[#1E3A8A] disabled:opacity-60 transition-colors rounded-sm px-6 py-4 font-bold text-sm uppercase tracking-widest"
                                    >
                                        {submitting ? "Sending…" : `Request ${service.title}`}
                                        <ArrowRight className="w-4 h-4" />
                                    </button>
                                </form>
                            )}
                        </div>
                    </div>
                </div>
            </section>

            {/* Related services */}
            {related.length > 0 && (
                <section className="bg-slate-50 py-20 border-t border-slate-200">
                    <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
                        <div className="mb-10 flex items-end justify-between">
                            <h3 className="font-serif text-3xl font-bold text-[#0A192F]">
                                Related services
                            </h3>
                            <Link to="/services" className="text-xs uppercase tracking-widest text-[#D4AF37] font-bold inline-flex items-center gap-2">
                                All services <ArrowRight className="w-4 h-4" />
                            </Link>
                        </div>
                        <div className="grid md:grid-cols-3 gap-6">
                            {related.map((r) => (
                                <Link
                                    key={r.id}
                                    to={`/services/${r.slug}`}
                                    data-testid={`related-service-${r.slug}`}
                                    className="exec-card p-7 group block"
                                >
                                    <ServiceIcon name={r.icon} className="w-5 h-5 text-[#D4AF37]" />
                                    <h4 className="font-serif text-xl font-bold mt-4 text-[#0A192F]">
                                        {r.title}
                                    </h4>
                                    <p className="mt-3 text-sm text-slate-600 leading-relaxed line-clamp-3">
                                        {r.description}
                                    </p>
                                    <span className="mt-5 inline-flex items-center gap-2 text-xs uppercase tracking-widest font-bold text-[#0A192F] group-hover:text-[#D4AF37] transition-colors">
                                        Learn more <ArrowRight className="w-3.5 h-3.5" />
                                    </span>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}
        </div>
    );
}
